import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  formatMoney,
  siguienteCuenta,
  type CuentaBancoConfig,
  type CuentaDeBanco,
  type Settings,
  type SettingKey,
} from '@abastece/shared';
import { cuentaDeLaTarjeta, cuentaDeTransferencias, cuentasAbiertas, cuentasDeLaTienda, nombreDeCuenta, saldosDeBancos } from '../lib/bancos.js';
import { useT } from '../i18n/index.js';
import { Campo, Interruptor, Selector } from './Campo.js';
import { enDolares } from './CuentasDeCaja.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/**
 * «Cuentas de banco» in «Configuración» (the owner, 2026-10-10: «we need the ability to setup
 * multiple bank accounts as that is closest to reality»): the shop's accounts, each opened with its
 * name and currency, renamed, closed and opened again; and where customers' transfers and card
 * payments go. The first in córdobas and the first in dollars are always there: what was booked to
 * «Bancos» and «Banco en dólares» before is theirs.
 *
 * Each change is the setting as a whole (`cuentasBanco`), as the trucks and the fixed expenses are.
 */
export function CuentasDeBanco({
  config,
  guardar,
}: {
  config: Settings;
  guardar: (clave: SettingKey, valor: unknown) => Promise<boolean>;
}) {
  const t = useT();
  const cuentas = cuentasDeLaTienda(config);
  const [editando, setEditando] = useState<CuentaDeBanco | 'nueva' | null>(null);
  const enCordobas = cuentasAbiertas(config, 'NIO');
  const nombre = (cuenta: string) => nombreDeCuenta(config, cuenta, t);

  return (
    <>
      <ul className="flex flex-col divide-y divide-slate-800" data-cuentas-banco>
        {cuentas.map((c) => (
          <li key={c.cuenta}>
            <button
              type="button"
              data-sin-icono="fila"
              data-cuenta-banco={c.cuenta}
              onClick={() => setEditando(c)}
              className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-xl px-2 py-2.5 text-left transition hover:bg-white/5 active:scale-[0.99]"
            >
              <span className="min-w-0 flex-1">
                <span className={`block break-words font-medium ${c.activa ? '' : 'text-slate-500 line-through'}`}>{nombre(c.cuenta)}</span>
                <span className="block text-xs text-slate-500">
                  {t(c.moneda === 'USD' ? 'bancos.enDolares' : 'bancos.enCordobas')}
                  {!c.activa && ` · ${t('bancos.cerrada')}`}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1.5 text-sm text-slate-400">
                <Ico icono="editar" className="size-4" />
                {t('negocio.cambiar')}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <div className="py-2">
        <button type="button" className="btn-secundario w-full" onClick={() => setEditando('nueva')} data-cuenta-agregar>
          <Ico icono="agregar" />
          {t('bancos.agregar')}
        </button>
      </div>
      {/* Where the money customers send goes: asked only when there is more than one place it could. */}
      {enCordobas.length > 1 ? (
        <>
          <div className="py-3">
            <Selector
              etiqueta={t('bancos.transferencias')}
              ayuda={t('bancos.transferenciasAyuda')}
              value={cuentaDeTransferencias(config)}
              onChange={(e) => void guardar('cuentaTransferencias', e.target.value)}
              data-cuenta-transferencias
            >
              {enCordobas.map((c) => (
                <option key={c.cuenta} value={c.cuenta}>
                  {nombre(c.cuenta)}
                </option>
              ))}
            </Selector>
          </div>
          <div className="py-3">
            <Selector
              etiqueta={t('bancos.tarjeta')}
              ayuda={t('bancos.tarjetaAyuda')}
              value={cuentaDeLaTarjeta(config)}
              onChange={(e) => void guardar('cuentaTarjeta', e.target.value)}
              data-cuenta-tarjeta
            >
              {enCordobas.map((c) => (
                <option key={c.cuenta} value={c.cuenta}>
                  {nombre(c.cuenta)}
                </option>
              ))}
            </Selector>
          </div>
        </>
      ) : (
        <p className="py-2 text-xs text-slate-500" data-cuenta-una-sola>
          {t('bancos.unaSola', { cuenta: nombre(cuentaDeTransferencias(config)) })}
        </p>
      )}

      {editando && (
        <HojaDeCuenta
          config={config}
          cuenta={editando === 'nueva' ? null : editando}
          onGuardar={(lista) => guardar('cuentasBanco', lista)}
          onCerrar={() => setEditando(null)}
        />
      )}
    </>
  );
}

/**
 * One account in a sheet: a new one's name and currency, or an account's name, and closing it or
 * opening it again. Closed only with nothing in it by the books and no payments going to it: the
 * money there would otherwise vanish from every screen that offers accounts.
 */
function HojaDeCuenta({
  config,
  cuenta,
  onGuardar,
  onCerrar,
}: {
  config: Settings;
  cuenta: CuentaDeBanco | null;
  onGuardar: (lista: CuentaBancoConfig[]) => Promise<boolean>;
  onCerrar: () => void;
}) {
  const t = useT();
  const nueva = cuenta === null;
  const [nombre, setNombre] = useState(cuenta?.nombre ?? '');
  const [moneda, setMoneda] = useState<'NIO' | 'USD'>(cuenta?.moneda ?? 'NIO');
  const [activa, setActiva] = useState(cuenta?.activa ?? true);
  const [intentado, setIntentado] = useState(false);
  /** The switch was turned off on one that cannot close: why is said under it. */
  const [quisoCerrar, setQuisoCerrar] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const saldo = useLiveQuery(async () => (cuenta ? (await saldosDeBancos([cuenta.cuenta]))[cuenta.cuenta] : undefined), [cuenta?.cuenta]);

  const lista = config.cuentasBanco;
  const siguiente = nueva ? siguienteCuenta(lista, moneda) : cuenta.cuenta;
  const limpio = nombre.trim();
  const repetido = cuentasDeLaTienda(config).some(
    (c) => c.cuenta !== siguiente && nombreDeCuenta(config, c.cuenta, t).toLocaleLowerCase() === limpio.toLocaleLowerCase(),
  );
  // «Banco» or «Banco en dólares», never named and left as it was: nothing to save. Anything else
  // saved needs the owner's name for it, so no phone's language ends up as the account's name.
  const igual = !nueva && cuenta.nombre === null && limpio === '' && activa === cuenta.activa;
  const error = igual
    ? null
    : !siguiente
      ? t('bancos.yaHayCinco')
      : limpio === ''
        ? t('bancos.faltaNombre')
        : repetido
          ? t('bancos.nombreRepetido')
          : null;
  const predeterminada = cuenta !== null && (cuentaDeTransferencias(config) === cuenta.cuenta || cuentaDeLaTarjeta(config) === cuenta.cuenta);
  const conDinero = saldo !== undefined && (saldo.cordobas !== 0 || saldo.dolares !== 0);
  // Why it cannot be closed, if it cannot: said under the switch, once turned, which stays as it was.
  const noSeCierra =
    cuenta && cuenta.activa
      ? predeterminada
        ? t('bancos.noSeCierraPredeterminada')
        : conDinero
          ? t('bancos.noSeCierraConSaldo')
          : null
      : null;

  async function aceptar() {
    setIntentado(true);
    if (igual) return onCerrar();
    // Closed before what it holds was read: wait for it, the switch may not be allowed.
    if (error || ocupado || !siguiente || (!activa && cuenta?.activa && saldo === undefined)) return;
    setOcupado(true);
    const fila: CuentaBancoConfig = { cuenta: siguiente, nombre: limpio, activa };
    const resto = lista.filter((c) => c.cuenta !== siguiente);
    if (await onGuardar([...resto, fila])) onCerrar();
    else setOcupado(false);
  }

  return (
    <Modal
      // Its name is in the field below, whole: a title is cut to one line.
      titulo={t(nueva ? 'bancos.nueva' : 'bancos.cual')}
      onCerrar={onCerrar}
      pie={
        // As «Configuración»'s other sheets: side by side down to 320 px.
        <div className="flex gap-3">
          <button type="button" className="btn-secundario flex-1 px-3" onClick={onCerrar}>
            <Ico icono="cancelar" />
            {t('comun.cancelar')}
          </button>
          <button type="button" className="btn-primario flex-1 px-3" disabled={ocupado} onClick={() => void aceptar()} data-cuenta-guardar>
            <Ico icono="guardar" />
            {ocupado ? t('comun.guardando') : t('comun.guardar')}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-hoja-cuenta>
        <Campo
          etiqueta={t('bancos.nombre')}
          ayuda={t('bancos.nombreAyuda')}
          error={intentado ? error : null}
          value={nombre}
          placeholder={nueva ? '' : nombreDeCuenta(config, cuenta.cuenta, t)}
          onChange={(e) => setNombre(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void aceptar()}
          maxLength={40}
          autoFocus
          autoComplete="off"
        />
        {nueva ? (
          <Selector
            etiqueta={t('bancos.moneda')}
            ayuda={t('bancos.monedaAyuda')}
            value={moneda}
            onChange={(e) => setMoneda(e.target.value as 'NIO' | 'USD')}
            data-cuenta-moneda
          >
            <option value="NIO">{t('bancos.cordobas')}</option>
            <option value="USD">{t('bancos.dolares')}</option>
          </Selector>
        ) : (
          <>
            {saldo && (
              <p className="text-sm text-slate-400" data-cuenta-saldo>
                {t('bancos.tiene', {
                  monto: cuenta.moneda === 'USD' ? `${enDolares(saldo.dolares)} · ${formatMoney(saldo.cordobas)}` : formatMoney(saldo.cordobas),
                })}
              </p>
            )}
            <div>
              <Interruptor
                etiqueta={t('bancos.abierta')}
                descripcion={t('bancos.abiertaAyuda')}
                checked={activa}
                onChange={(v) => {
                  if (!v && noSeCierra) {
                    setQuisoCerrar(true);
                    return;
                  }
                  setActiva(v);
                }}
              />
              {quisoCerrar && noSeCierra && (
                <p className="mt-2 text-xs text-amber-300" data-cuenta-no-se-cierra>
                  {noSeCierra}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
